import { useState, useCallback, useRef, KeyboardEvent } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { NavigationGroup } from '../../constants/navigation-groups';

interface SidebarGroupProps {
  group: NavigationGroup;
  onItemClick?: () => void;
}

export function SidebarGroup({ group, onItemClick }: SidebarGroupProps) {
  const [isExpanded, setIsExpanded] = useState(group.defaultExpanded);
  const location = useLocation();
  const listRef = useRef<HTMLDivElement>(null);

  const isActive = (path: string) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  const hasActiveItem = group.items.some((item) => isActive(item.path));

  const toggleExpanded = useCallback(() => {
    setIsExpanded((prev) => !prev);
  }, []);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLButtonElement>) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        toggleExpanded();
      }
    },
    [toggleExpanded],
  );

  const GroupIcon = group.icon;

  // Single-item groups (like Dashboard) render the item directly
  if (!group.isCollapsible && group.items.length === 1) {
    const item = group.items[0];
    const active = isActive(item.path);
    const ItemIcon = item.icon;
    return (
      <div className="mb-1">
        <Link
          to={item.path}
          onClick={onItemClick}
          className={cn(
            'flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-150',
            active
              ? 'bg-primary-50 dark:bg-primary-950/50 text-primary-700 dark:text-primary-300 border-l-2 border-primary-500'
              : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-gray-100',
          )}
          aria-current={active ? 'page' : undefined}
        >
          <ItemIcon
            className={cn('w-4.5 h-4.5 flex-shrink-0', active ? 'text-primary-600 dark:text-primary-400' : '')}
          />
          <span>{item.name}</span>
        </Link>
      </div>
    );
  }

  return (
    <div className="mb-1" role="group" aria-labelledby={`nav-group-${group.id}`}>
      {group.isCollapsible ? (
        <button
          id={`nav-group-${group.id}`}
          onClick={toggleExpanded}
          onKeyDown={handleKeyDown}
          aria-expanded={isExpanded}
          aria-controls={`nav-group-items-${group.id}`}
          className={cn(
            'w-full flex items-center justify-between px-3 py-2 text-xs font-semibold uppercase tracking-wider rounded-lg transition-colors',
            'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-1',
            hasActiveItem
              ? 'text-primary-600 dark:text-primary-400'
              : 'text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300',
          )}
        >
          <span className="flex items-center gap-2">
            <GroupIcon className="w-3.5 h-3.5" />
            {group.label}
          </span>
          <ChevronDown
            className={cn(
              'w-3.5 h-3.5 transition-transform duration-200',
              isExpanded ? 'rotate-0' : '-rotate-90',
            )}
            aria-hidden="true"
          />
        </button>
      ) : (
        <div
          id={`nav-group-${group.id}`}
          className="px-3 py-2 text-xs font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500"
        >
          <span className="flex items-center gap-2">
            <GroupIcon className="w-3.5 h-3.5" />
            {group.label}
          </span>
        </div>
      )}

      <div
        id={`nav-group-items-${group.id}`}
        ref={listRef}
        role="list"
        className={cn(
          'overflow-hidden transition-all duration-200 ease-in-out',
          isExpanded || !group.isCollapsible ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0',
        )}
      >
        <div className="ml-2 space-y-0.5">
          {group.items.map((item) => {
            const active = isActive(item.path);
            const ItemIcon = item.icon;
            return (
              <Link
                key={item.id}
                to={item.path}
                role="listitem"
                onClick={onItemClick}
                className={cn(
                  'flex items-center gap-3 px-3 py-2 rounded-lg text-sm transition-all duration-150',
                  active
                    ? 'bg-primary-50 dark:bg-primary-950/50 text-primary-700 dark:text-primary-300 font-medium border-l-2 border-primary-500'
                    : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-gray-100',
                )}
                aria-current={active ? 'page' : undefined}
              >
                <ItemIcon
                  className={cn('w-4 h-4 flex-shrink-0', active ? 'text-primary-600 dark:text-primary-400' : '')}
                />
                <span>{item.name}</span>
                {item.badge != null && item.badge > 0 && (
                  <span className="ml-auto bg-red-500 text-white text-xs rounded-full px-1.5 py-0.5 min-w-[20px] text-center">
                    {item.badge}
                  </span>
                )}
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
}
